import { useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { filter, findIndex, includes, map } from 'lodash'
import { FC, SyntheticEvent, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FreeLabel } from '../../../../components/FreeLabel'
import { FREE_MODEL_LIST } from '../../../../constants/constants'
import { useProfileStore } from '../../../../store/storeProfile'
import { useSettingStore } from '../../../../store/storeSetting'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { LLMModelDTO } from '../../../../swagger/dto'
import { theme } from '../../../../theme'
import { ModelIcon } from '../../MainPage/components/ModelIcon'
import { ModelSpecializationIcon } from '../../MainPage/components/ModelSpecializationIcon'
import { SpecializationSelect } from '../../MainPage/components/SpecializationSelect'
import ImageCircleIcon from './../../../../assets/icons/ImageCircle.svg'
import MemoIcon from './../../../../assets/icons/Memo.svg'
import MicrophoneIcon from './../../../../assets/icons/Microphone.svg'
import ShieldCheck from './../../../../assets/icons/ShieldCheck.svg?react'
import TablerIconVideo from './../../../../assets/icons/TablerIconVideo.svg'
import VolumeMaxIcon from './../../../../assets/icons/VolumeMax.svg'
const notificationDone = 'Выбор используемых моделей успешно сохранен'

const columns = [
    { label: 'Название', maxWidth: 400, key: 'name' },
    { label: 'Тип обработки', minWidth: 150, key: 'type' },
    {
        label: 'Страна',
        minWidth: 120,
        key: 'country',
    },
    {
        label: 'Цена запроса',
        minWidth: 160,
        key: 'price',
    },
    {
        label: 'Использование',
        minWidth: 120,
        key: 'usage',
    },
]

const nameType = {
    TEXT: 'текста',
    IMAGE: 'изображений',
    VIDEO: 'видео',
    SPEECH: 'речи',
    AUDIO: 'аудио',
}

export type ModelSetupFormValues = { model: string[] }

type Props = {
    initialValues: ModelSetupFormValues
    modelList: LLMModelDTO[]
}

const filterBySpecialization = (items, specializations) => {
    return filter(items, (el) => includes(specializations, el.model_specialization))
}

export const ModelSetupForm: FC<Props> = ({ initialValues, modelList }) => {
    const [tab, setTab] = useState('TEXT')
    const [modelListBySpecialization, setModelListBySpecialization] = useState(modelList)
    const [openTooltipId, setOpenTooltipId] = useState<string | null>(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const { setStateNotification, setOpenLoader } = useWrapperStore((state) => state)
    const { isFullAccess } = useProfileStore()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const { updateModel, isLoadingUpdateModel, errorUpdateModel, isDoneUpdateModel } =
        useSettingStore((state) => state)

    useEffect(() => {
        if (isDoneUpdateModel) {
            setOpenLoader(false)
            setStateNotification({ isOpen: true, text: notificationDone, isError: false })
        }
    }, [isDoneUpdateModel])

    useEffect(() => {
        const specialization = searchParams.get('specialization')?.split(',')
        if (tab === 'TEXT' && specialization) {
            const data = filterBySpecialization(modelList, specialization)
            setModelListBySpecialization(data)
        } else {
            setModelListBySpecialization(modelList)
        }
    }, [tab, modelList, searchParams.get('specialization')])

    const { handleSubmit, values, handleBlur, handleChange, resetForm } = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        onSubmit: async (values) => {
            const { model } = values
            const data = map(modelListBySpecialization, (value) => {
                return {
                    model_slug: value.slug,
                    is_restricted: !includes(model, value.slug),
                }
            })
            setOpenLoader(true)
            await updateModel({ restrictions: data }, nameType[tab])
        },
    })

    useEffect(() => {
        if (errorUpdateModel) {
            setOpenLoader(false)
            setStateNotification({ isOpen: true, text: errorUpdateModel, isError: true })
        }
    }, [errorUpdateModel])

    const handleChangeTabs = (event: SyntheticEvent, tab: string) => {
        setTab(tab)
    }

    const handleCloseTooltip = () => setOpenTooltipId(null)

    const ModelTable = (
        <TableContainer
            sx={{ padding: '0', margin: '12px 0' }}
            onScroll={handleCloseTooltip}>
            <Table stickyHeader aria-label="sticky table">
                <TableHead>
                    <TableRow>
                        {columns.map((column, index) => (
                            <TableCell
                                sx={{ background: '#F5F5F5' }}
                                key={column.key}
                                style={{
                                    minWidth: column.minWidth,
                                    borderTopLeftRadius: index === 0 ? '16px' : 0,
                                    borderTopRightRadius: index === 4 ? '16px' : 0,
                                }}>
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {filter(modelListBySpecialization, { model_type: tab })?.map((row) => {
                        const isDisabled =
                            !isFullAccess && findIndex(FREE_MODEL_LIST, { key: row.slug }) < 0

                        return (
                            <TableRow
                                hover
                                tabIndex={-1}
                                key={row.slug}
                                sx={{ opacity: isDisabled ? '0.5' : 1 }}>
                                <TableCell key={`${row.slug}_name`}>
                                    <Stack direction="row" spacing={1}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: '4px',
                                            }}>
                                            <ModelIcon agentKey={row.slug} />
                                            <Typography
                                                variant="caption1"
                                                component="p"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                {row.title}
                                            </Typography>
                                            {row.model_specialization && (
                                                <Stack
                                                    sx={{
                                                        background: '#F4F4F6',
                                                        borderRadius: '6px',
                                                        '&:hover': {
                                                            borderRadius: '6px',
                                                        },
                                                        width: '20px',
                                                        height: '20px',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}>
                                                    <ModelSpecializationIcon
                                                        style={{ width: '15px', height: '15px' }}
                                                        specialization={row.model_specialization}
                                                    />
                                                </Stack>
                                            )}
                                            {row.is_protected && (
                                                <Tooltip
                                                    enterTouchDelay={0}
                                                    title="Защищенная нейросеть">
                                                    <IconButton
                                                        sx={{
                                                            p: 0,
                                                            width: '20px',
                                                            height: '20px',
                                                        }}>
                                                        <ShieldCheck
                                                            style={{
                                                                color: '#5A5C5E',
                                                                height: '16px',
                                                                width: '16px',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Stack>

                                    <Typography variant="caption2" sx={{ color: '#9195A1' }}>
                                        {row?.description}
                                    </Typography>
                                </TableCell>
                                <TableCell key={`${row.slug}_type`}>
                                    {filter(
                                        [
                                            {
                                                content_type: 'TEXT',
                                                is_supported: true,
                                            },
                                            ...row.supported_content,
                                        ],
                                        {
                                            is_supported: true,
                                        },
                                    )?.map((item, index) => {
                                        return (
                                            <span
                                                key={`${row.slug}_type_${index}`}
                                                style={{
                                                    marginRight: '8px',
                                                    background: '#F4F4F6',
                                                    padding: '4px',
                                                    borderRadius: '6px',
                                                }}>
                                                {item.content_type === 'IMAGE' && 'Изображения'}
                                                {item.model_type === 'VIDEO' && 'Видео'}
                                                {item.content_type === 'AUDIO' && 'Аудио'}
                                                {item.content_type === 'AUDIO_VOICE' && 'Речь'}
                                                {item.content_type === 'TEXT' && 'Текст'}
                                                {item.content_type === 'DOCUMENT' && 'Документ'}
                                            </span>
                                        )
                                    })}
                                </TableCell>
                                <TableCell key={`${row.slug}_country`}>
                                    {row.legal.origin.name ?? ''}
                                </TableCell>
                                <TableCell key={`${row.slug}_price`}>
                                    {row.estimated_price === 'Бесплатно' ? (
                                        <FreeLabel />
                                    ) : (
                                        (row.estimated_price ?? '')
                                    )}
                                </TableCell>
                                <TableCell key={`${row.slug}_usage`}>
                                    <Tooltip
                                        open={openTooltipId === row.slug}
                                        onOpen={() => setOpenTooltipId(row.slug)}
                                        onClose={handleCloseTooltip}
                                        enterTouchDelay={0}
                                        title="Использовать модель в настройках формирования запроса"
                                        placement="top-start">
                                        <FormControlLabel
                                            disabled={isDisabled}
                                            sx={{ mb: 1 }}
                                            id="model"
                                            name="model"
                                            control={
                                                <Checkbox
                                                    checked={values.model.includes(row.slug)}
                                                    value={row.slug}
                                                />
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )

    const ModelList = filter(modelListBySpecialization, { model_type: tab })?.map((model) => {
        const isDisabled = !isFullAccess && findIndex(FREE_MODEL_LIST, { key: model.slug }) < 0

        return (
            <Grid
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    '& + &': {
                        borderTop: '1px solid #E9EAED',
                    },
                }}>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '6px',
                        opacity: isDisabled ? '0.5' : 1,
                    }}>
                    <ModelIcon agentKey={model.slug} />
                    <Typography variant="h6" sx={{ color: '#121317' }}>
                        {model.title}
                    </Typography>
                    {model.model_specialization && (
                        <Stack
                            sx={{
                                background: '#F4F4F6',
                                borderRadius: '6px',
                                '&:hover': {
                                    borderRadius: '6px',
                                },
                                width: '20px',
                                height: '20px',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}>
                            <ModelSpecializationIcon
                                style={{ width: '15px', height: '15px' }}
                                specialization={model.model_specialization}
                            />
                        </Stack>
                    )}
                    {model.is_protected && (
                        <Tooltip enterTouchDelay={0} title="Защищенная нейросеть">
                            <IconButton sx={{ p: 0, width: '20px', height: '20px' }}>
                                <ShieldCheck
                                    style={{
                                        color: '#5A5C5E',
                                        height: '16px',
                                        width: '16px',
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                    <FormControlLabel
                        disabled={isDisabled}
                        sx={{
                            marginLeft: 'auto',
                            marginRight: '0',
                        }}
                        id="model"
                        name="model"
                        control={
                            <Checkbox
                                checked={values.model.includes(model.slug)}
                                value={model.slug}
                            />
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Stack>
                <Typography variant="caption2" sx={{ color: '#9195A1', marginBottom: '16px' }}>
                    {model?.description}
                </Typography>
                <Grid
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        marginBottom: '28px',
                        gap: '8px',
                    }}>
                    {filter(
                        [
                            {
                                content_type: 'TEXT',
                                is_supported: true,
                            },
                            ...model.supported_content,
                        ],
                        {
                            is_supported: true,
                        },
                    )?.map((item, index) => {
                        return (
                            <span
                                key={`model_list_${model.slug}_${index}`}
                                style={{
                                    background: '#F4F4F6',
                                    padding: '4px',
                                    borderRadius: '6px',
                                }}>
                                {item.content_type === 'IMAGE' && 'Изображения'}
                                {item.model_type === 'VIDEO' && 'Видео'}
                                {item.content_type === 'AUDIO' && 'Аудио'}
                                {item.content_type === 'AUDIO_VOICE' && 'Речь'}
                                {item.content_type === 'TEXT' && 'Текст'}
                                {item.content_type === 'DOCUMENT' && 'Документ'}
                            </span>
                        )
                    })}
                </Grid>
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '24px',
                    }}>
                    <Typography variant="body2" sx={{ color: '#9195A1' }}>
                        Цена запроса:
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#121317' }}>
                        {model.estimated_price === 'Бесплатно' ? (
                            <FreeLabel />
                        ) : (
                            (model.estimated_price ?? '')
                        )}
                    </Typography>
                </Grid>
            </Grid>
        )
    })

    return (
        <>
            <Grid sx={{ padding: '0 16px 24px', textAlign: 'left' }}>
                <Typography variant="h4" sx={{ color: '#121317', mb: '4px' }}>
                    Выбор используемых моделей
                </Typography>
                <Typography variant="caption2" component="p">
                    Выберите языковые модели, которые будут использоваться в настройках формирования
                    запроса
                </Typography>

                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    sx={{
                        justifyContent: 'space-between',
                    }}>
                    <Tabs
                        value={tab}
                        onChange={handleChangeTabs}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{ borderBottom: 'none', padding: 0, mt: 2 }}>
                        <Tab label="Текст" value="TEXT" icon={<MemoIcon />} iconPosition="start" />
                        <Tab
                            label="Изображения"
                            value="IMAGE"
                            icon={<ImageCircleIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Видео"
                            value="VIDEO"
                            icon={<TablerIconVideo />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Речь"
                            value="SPEECH"
                            icon={<VolumeMaxIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Аудио"
                            value="AUDIO"
                            icon={<MicrophoneIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                    {tab === 'TEXT' && <SpecializationSelect />}
                </Stack>
                <form onSubmit={handleSubmit}>
                    <Paper
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            borderRadius: isMobile ? '0' : '28px',
                        }}>
                        {filter(modelList, { model_type: tab }).length ? (
                            !isMobile ? (
                                ModelTable
                            ) : (
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        padding: '16px',
                                        border: '1px solid #E9EAED',
                                        borderRadius: '16px',
                                        overflow: 'auto',
                                        // maxHeight: '380px',
                                    }}>
                                    {ModelList}
                                </Grid>
                            )
                        ) : (
                            <Box
                                sx={{
                                    marginLeft: '8px',
                                    marginBottom: '8px',
                                    borderRadius: '12px',
                                    background:
                                        ' linear-gradient(348deg, #E2FF94 32%, #CFF28D 49%, #F5FFEE 98%)',
                                    padding: '24px',
                                    border: '1px solid #E2FF94',
                                }}>
                                <Typography>Обнови тариф и получи доступ к моделям</Typography>
                            </Box>
                        )}
                    </Paper>

                    {!!filter(modelListBySpecialization, { model_type: tab }).length && (
                        <Button
                            fullWidth={isMobile}
                            type="submit"
                            disabled={isLoadingUpdateModel}
                            variant="contained"
                            sx={{ mt: 2 }}>
                            Сохранить
                        </Button>
                    )}
                </form>
            </Grid>
        </>
    )
}
